import React from 'react';
import { RefreshCw, Clock, CalendarClock } from 'lucide-react';
import { PricingPeriod } from '../../types/pricing';

interface PricingPeriodToggleProps {
  period: PricingPeriod;
  onPeriodChange: (period: PricingPeriod) => void;
  /** Effective date of a published-but-not-yet-live change, if any. */
  announcedDate?: string | null;
  /** Whether any plan still has a pre-renewal rate worth showing. */
  hasPreRenewal?: boolean;
}

const fmt = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

export const PricingPeriodToggle: React.FC<PricingPeriodToggleProps> = ({
  period,
  onPeriodChange,
  announcedDate = null,
  hasPreRenewal = false,
}) => {
  // Nothing to compare against — don't render a toggle with one option.
  if (!hasPreRenewal && !announcedDate) return null;

  const base =
    'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all';
  const on = 'bg-white shadow-sm';
  const off = 'text-gray-600 hover:text-gray-900';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">Pricing:</label>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {hasPreRenewal && (
            <button
              onClick={() => onPeriodChange('preRenewal')}
              className={`${base} ${period === 'preRenewal' ? `${on} text-gray-900` : off}`}
            >
              <Clock className="w-4 h-4" />
              Before renewal
            </button>
          )}

          <button
            onClick={() => onPeriodChange('atRenewal')}
            className={`${base} ${period === 'atRenewal' ? `${on} text-blue-600` : off}`}
          >
            <RefreshCw className="w-4 h-4" />
            At renewal
          </button>

          {announcedDate && (
            <button
              onClick={() => onPeriodChange('announced')}
              className={`${base} ${period === 'announced' ? `${on} text-orange-600` : off}`}
            >
              <CalendarClock className="w-4 h-4" />
              <span>
                Announced
                <span className="ml-1 text-xs">(from {fmt(announcedDate)})</span>
              </span>
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 max-w-xl">
        {period === 'preRenewal' &&
          'The rate on an existing term. Customers keep it until their subscription renews.'}
        {period === 'atRenewal' &&
          'Microsoft list price in effect today. This is what a new purchase or a renewal costs — existing customers move to it in their renewal month.'}
        {period === 'announced' && announcedDate &&
          `Published by Microsoft, effective ${fmt(announcedDate)}. Not yet billable.`}
      </p>
    </div>
  );
};
